import { useState } from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * Financial term definitions for educational tooltips.
 * These help students learn while playing the game.
 */
export const FINANCIAL_TERMS = {
  // Price & Performance
  market_cap: {
    term: "Market Cap",
    definition: "The total value of all a company's shares. Calculated by multiplying the stock price by the number of shares outstanding.",
    example: "If a company has 1 million shares at $100 each, its market cap is $100 million."
  },
  pe_ratio: {
    term: "P/E Ratio",
    definition: "Price-to-Earnings ratio compares a stock's price to its earnings per share. Lower P/E may indicate value, higher P/E may indicate growth expectations.",
    example: "A P/E of 20 means investors pay $20 for every $1 of earnings."
  },
  volume: {
    term: "Volume",
    definition: "The number of shares or contracts traded during a specific period. High volume indicates strong interest in a stock.",
    example: "Volume of 10M means 10 million shares changed hands today."
  },
  volatility: {
    term: "Volatility",
    definition: "How much a price fluctuates over time. Higher volatility means bigger price swings (more risk, more potential reward).",
    example: "Crypto tends to be more volatile than blue-chip stocks."
  },
  
  // Portfolio Concepts
  diversification: {
    term: "Diversification",
    definition: "Spreading investments across different assets to reduce risk. If one investment drops, others may stay stable or rise.",
    example: "Owning stocks, crypto, and ETFs is more diversified than owning only tech stocks."
  },
  allocation: {
    term: "Allocation",
    definition: "How your money is divided among different investments or asset types.",
    example: "60% stocks, 30% crypto, 10% commodities is an allocation strategy."
  },
  holdings: {
    term: "Holdings",
    definition: "The investments you currently own in your portfolio. You need exactly 10 to start scoring.",
    example: "Your holdings might include Apple, Bitcoin, and a Gold ETF."
  },
  power_play: {
    term: "Power Play",
    definition: "Your selected asset that earns 3x points this week. Choose wisely - pick the one you think will perform best!",
    example: "If your Power Play gains 5%, you earn 3x the normal points for that gain."
  },
  
  // Asset Types
  stock: {
    term: "Stock",
    definition: "A share of ownership in a company. When you buy stock, you own a tiny piece of that company.",
    example: "Buying 1 share of Apple means you own a small part of Apple Inc."
  },
  etf: {
    term: "ETF",
    definition: "Exchange-Traded Fund - a basket of stocks or assets bundled together. One ETF can give you exposure to many companies.",
    example: "SPY is an ETF that tracks the S&P 500, so you own a bit of 500 companies."
  },
  crypto: {
    term: "Cryptocurrency",
    definition: "Digital currency using blockchain technology. Known for high volatility and 24/7 trading.",
    example: "Bitcoin and Ethereum are the largest cryptocurrencies by market cap."
  },
  commodity: {
    term: "Commodity",
    definition: "Physical goods like gold, oil, or agricultural products that can be traded.",
    example: "Gold is considered a 'safe haven' commodity during market uncertainty."
  },
  
  // Performance Metrics
  gain_loss: {
    term: "Weekly P&L",
    definition: "Profit & Loss — the percentage your portfolio gained or lost this week based on price changes since the week opened.",
    example: "A +2.5% Weekly P&L means your portfolio grew by 2.5% this week."
  },
  total_return: {
    term: "Total Return",
    definition: "The overall percentage gain or loss of your entire portfolio since you started playing.",
    example: "If you started with V$100,000 and now have V$105,000, your total return is +5%."
  },
  
  // Game Concepts
  trading_window: {
    term: "Trading Window",
    definition: "The weekly period when you can buy and sell assets. Closes Sunday night before scoring.",
    example: "Make your trades before the window closes to lock in your portfolio for scoring."
  },
  weekly_score: {
    term: "Weekly Score",
    definition: "Points earned based on how your holdings performed this week. Gains = positive points, losses = negative points.",
    example: "A +5% stock gain might earn you 7 points, while a -5% loss costs you 4 points."
  },
  chip: {
    term: "Chip",
    definition: "A special power-up you can use once per season. Each chip provides a unique advantage.",
    example: "The Wildcard chip lets you make unlimited trades without penalty for one week."
  },
  season: {
    term: "Season",
    definition: "A quarter-long competition period (13 weeks). Your cumulative points determine your ranking.",
    example: "Season 2026-Q1 runs from January to March 2026."
  },

  // Scoring Terms
  base_performance: {
    term: "Base Performance",
    definition: "Points earned from each instrument's price movement during the week. Each asset is scored individually using tiered brackets — bigger moves earn more points.",
    example: "A stock that gained +4% this week earns 4 base points. Crypto gains are scored at half the rate."
  },
  bonuses: {
    term: "Bonuses",
    definition: "Extra points on top of base performance. Includes your Holding Bonus and any multiplier boosts from Power Play or chips.",
    example: "With 10 holdings (+10 bonus) and a 3x Power Play on a +5% gainer, you could earn 21 extra bonus points."
  },
  holding_bonus: {
    term: "Holding Bonus",
    definition: "Earn +1 point for each active instrument in your portfolio, up to 10 points. Encourages building a full portfolio rather than concentrating bets.",
    example: "10 instruments held = +10 holding bonus. 7 instruments = only +7."
  },
  trade_penalty: {
    term: "Trade Penalty",
    definition: "You get 7 free trades per week. Each additional trade costs -10 points. Plan your moves carefully!",
    example: "8 trades in a week = 1 extra trade = -10 points penalty."
  },
  diversity_penalty: {
    term: "Diversity Penalty",
    definition: "If 100% of your portfolio is in a single asset class (e.g., all stocks), you receive a -5 point penalty. Mix it up!",
    example: "All 10 holdings are stocks → -5 points. Add 1 crypto or ETF → penalty removed."
  },
  portfolio_value: {
    term: "Portfolio Value",
    definition: "The total value of your portfolio in Virtual Dollars (V$). This is the combined value of all your holdings plus your remaining cash.",
    example: "V$85,000 in holdings + V$15,000 cash = V$100,000 portfolio value."
  },
  virtual_dollars: {
    term: "Virtual Dollars (V$)",
    definition: "The in-game currency used in Trader Titans. Every player starts with V$100,000. V$ tracks real market movements but isn't real money.",
    example: "V$100,000 starting budget = real market exposure, zero financial risk."
  },
  cash_balance: {
    term: "Cash Balance",
    definition: "The amount of V$ you haven't invested yet. Use it to buy new instruments. You keep any uninvested cash in your portfolio value.",
    example: "If you invested V$80,000, your cash balance is V$20,000."
  },
  global_rank: {
    term: "Global Rank",
    definition: "Your position on the season leaderboard based on cumulative weekly scores. Compete against all players worldwide.",
    example: "Rank #1 means you have the highest total points this season."
  },
  free_trades: {
    term: "Free Trades",
    definition: "You can make 1 trade per day (7 per week) for free. Extra trades cost -10 points each. Use them wisely!",
    example: "Traded Monday and Tuesday = 5 free trades left this week."
  },
  daily_change: {
    term: "Daily Change",
    definition: "How much an instrument's price changed today, shown as a percentage.",
    example: "+1.5% means the price went up 1.5% since market open today."
  },
  weekly_change: {
    term: "Weekly Change",
    definition: "How much an instrument's price changed since the scoring week opened. This is what determines your score.",
    example: "wk: +3.2% means the price is up 3.2% since the week started. This is what your score is based on."
  },
  season_total: {
    term: "Season Total",
    definition: "Your cumulative points across all scored weeks this season. This is what determines your leaderboard rank.",
    example: "Week 1: 35pts + Week 2: 42pts + Week 3: 28pts = 105 season total."
  },
  top_performers: {
    term: "Top Performers",
    definition: "Players with the highest scores this week. Study their strategies to improve your own game!",
    example: "The #1 performer this week scored 65 points with a well-diversified portfolio."
  }
};

/**
 * Tooltip component that shows financial term definitions on hover.
 */
export const FinancialTooltip = ({ 
  termKey, 
  children, 
  showIcon = true,
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const term = FINANCIAL_TERMS[termKey];
  
  if (!term) {
    return <span className={className}>{children}</span>;
  }
  
  return (
    <span 
      className={`relative inline-flex items-center gap-1 cursor-help ${className}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      data-testid={`tooltip-${termKey}`}
    >
      {children}
      {showIcon && (
        <HelpCircle className="h-3.5 w-3.5 text-slate-500 hover:text-blue-400 transition-colors" />
      )}
      
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
            <div className="border-8 border-transparent border-t-slate-800"></div>
          </div>
          <h4 className="font-semibold text-white text-sm mb-1">{term.term}</h4>
          <p className="text-slate-300 text-xs leading-relaxed mb-2">{term.definition}</p>
          <p className="text-slate-400 text-xs italic">
            <span className="text-blue-400">Example:</span> {term.example}
          </p>
        </div>
      )}
    </span>
  );
};

/**
 * Inline tooltip for use within text.
 */
export const InlineTooltip = ({ termKey, text }) => {
  const [isOpen, setIsOpen] = useState(false);
  const term = FINANCIAL_TERMS[termKey];
  
  if (!term) return <span>{text || termKey}</span>;
  
  return (
    <span 
      className="relative inline-block border-b border-dashed border-blue-400/50 cursor-help"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <span className="text-blue-400">{text || term.term}</span>
      
      {isOpen && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
          <h4 className="font-semibold text-white text-sm mb-1">{term.term}</h4>
          <p className="text-slate-300 text-xs leading-relaxed">{term.definition}</p>
        </div>
      )}
    </span>
  );
};

export default FinancialTooltip;
